<script setup>
import moment from "moment";
import { computed } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  total: {
    type: Number,
    default: 0,
  },
  pageSize: {
    type: Number,
    default: 25,
  },
  current: {
    type: Number,
    default: 1,
  },
});

const emit = defineEmits(["change"]);

const columns = [
  {
    title: "#",
    dataIndex: "index",
    key: "index",
    width: 60,
    sorter: false,
  },
  {
    title: "Employee",
    dataIndex: "employee_name",
    key: "employee_name",
    sorter: true,
  },
  {
    title: "Leave Type",
    dataIndex: "leave_type_name",
    key: "leave_type_name",
    sorter: true,
  },
  {
    title: "From",
    dataIndex: "from_date",
    key: "from_date",
    sorter: true,
  },
  {
    title: "To",
    dataIndex: "to_date",
    key: "to_date",
    sorter: true,
  },
  {
    title: "Days",
    dataIndex: "days",
    key: "days",
    sorter: true,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
  },
  {
    title: "Covered by",
    dataIndex: "delegate_name",
    key: "delegate_name",
    sorter: false,
  },
  {
    title: "Approved By",
    dataIndex: "approved_by_name",
    key: "approved_by_name",
    sorter: true,
  },
];

const STATUS_LABELS = {
  New: "Pending Manager",
  ManagerApproved: "Awaiting HR",
  Approved: "Approved",
  Declined: "Declined",
  Cancelled: "Cancelled",
};

function humanStatus(status) {
  return STATUS_LABELS[status] || status || "Pending Manager";
}

function formatDate(d) {
  return d ? moment(d).format("DD MMM YYYY") : "-";
}

const dataSource = computed(() =>
  (props.rows || []).map((r, idx) => ({
    key: r.id ?? idx,
    index: idx + 1 + (props.current - 1) * props.pageSize,
    employee_name: r.employee_name || "-",
    leave_type_name: r.leave_type?.name || r.leave_type_name || "-",
    from_date: formatDate(r.from_date) ?? r.from_date_label ?? "",
    to_date: formatDate(r.to_date) ?? r.to_date_label ?? "",
    days: r.half_day ? `${r.days ?? 0} (half day)` : (r.days ?? 0),
    status: r.status ?? "",
    approved_by_name: r.approved_by_name || "",
    delegate_name: r.delegate_name || "—",
    raw: r,
  }))
);

const paginationConfig = computed(() => ({
  total: props.total,
  current: props.current,
  pageSize: props.pageSize,
  showSizeChanger: true,
  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
}));

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}
</script>

<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="paginationConfig"
    @change="onChange"
  >
    <template #bodyCell="{ column, record }">
      <!-- Employee -->
      <template v-if="column.key === 'employee_name'">
        <div class="d-flex align-items-center">
          <DqAvatar
            :src="record.raw?.avatar_url || ''"
            :name="record.employee_name || ''"
            size-class="avatar-sm"
            shape-class="rounded-circle"
            class="me-2"
          />
          <div>
            <p class="mb-0 text-dark">{{ record.employee_name }}</p>
            <span class="fs-12 text-muted">
              {{
                record.raw?.employee?.designation?.name
                  ? record.raw.employee.designation.name
                  : ""
              }}
            </span>
          </div>
        </div>
      </template>

      <!-- Status badge -->
      <template v-else-if="column.key === 'status'">
        <div>
          <span
            class="badge badge-xs d-inline-flex align-items-center"
            :class="[
              record.status === 'Approved'
                ? 'badge-soft-success'
                : record.status === 'Declined' || record.status === 'Cancelled'
                ? 'badge-soft-danger'
                : 'badge-soft-warning',
            ]"
          >
            <i class="ti ti-point-filled me-1"></i>{{ humanStatus(record.status) }}
          </span>
          <div
            v-if="record.raw?.decline_reason && (record.status === 'Declined' || record.status === 'Cancelled')"
            class="text-danger fs-12 mt-1"
          >
            {{ record.raw.decline_reason }}
          </div>
        </div>
      </template>

      <!-- Default: use normal value -->
      <template v-else>
        {{ record[column.dataIndex] }}
      </template>
    </template>
  </a-table>
</template>

<style scoped>
@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table) {
    min-width: 920px;
  }

  :deep(.dq-report-table .ant-table-cell) {
    vertical-align: top;
    white-space: normal;
  }
}
</style>
